import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Loader2 } from "lucide-react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { type GenericId as Id } from "convex/values";

interface DeviceLevelSelectorProps {
  deviceId: string;
  deviceConfig?: string;
  deviceName: string;
  compact?: boolean;
}

export const DeviceLevelSelector = ({ deviceId, deviceConfig, deviceName, compact = false }: DeviceLevelSelectorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLevelId, setSelectedLevelId] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);
  const { toast } = useToast();

  // Fetch device details to get current level information
  const deviceDetails = useConvexQuery(api.devices.get.details, {
    device: deviceId as Id<"devices">
  });

  // Fetch all levels for selection
  const levelsData = useConvexQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  // Update device level mutation
  const updateDeviceLevelMutation = useConvexMutation(api.devices.level.update);

  const levels = levelsData?.page || [];
  const isLoadingLevels = levelsData === undefined;
  const isLoadingDevice = deviceDetails === undefined;
  const isLoading = isLoadingLevels || isLoadingDevice;

  // Get current level from device details
  const currentLevel = deviceDetails?.level ? {
    id: deviceDetails.level,
    name: deviceDetails.level
  } : null;

  // Use deviceConfig from props or from deviceDetails
  const effectiveDeviceConfig = deviceConfig || deviceDetails?.device?.deviceConfig;

  const handleAssignLevel = async () => {
    if (!selectedLevelId) {
      toast({
        title: "Error",
        description: "Please select a level",
        variant: "destructive",
      });
      return;
    }

    // Handle "None" selection
    if (selectedLevelId === "none") {
      toast({
        title: "Info",
        description: "Level removal will be implemented when the API supports it",
      });
      return;
    }

    // Find the level object by name to get its _id
    const selectedLevel = levels.find(level => level.name === selectedLevelId);
    if (!selectedLevel) {
      toast({
        title: "Error",
        description: "Selected level not found",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAssigning(true);
      console.log(`Assigning device ${deviceId} to level ${selectedLevelId}`);
      
      await updateDeviceLevelMutation({
        device: deviceId as Id<"devices">,
        level: selectedLevel._id as Id<"levels">
      });

      toast({
        title: "Success",
        description: "Device level assigned successfully",
      });

      setIsEditing(false);
      setSelectedLevelId("");
      
    } catch (error) {
      console.error('Error assigning level:', error);
      toast({
        title: "Error",
        description: "Failed to assign device level. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setSelectedLevelId(currentLevel?.id || "");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedLevelId("");
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {currentLevel ? (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {currentLevel.name}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-gray-500">
            No Level
          </Badge>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (!deviceDetails) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-red-500">Device not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">Device Level</h4>
        {currentLevel && !isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEditClick}
            className="text-blue-600 hover:text-blue-700"
          >
            Change
          </Button>
        )}
      </div>

      {currentLevel && !isEditing ? (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">{currentLevel.name}</p>
              <p className="text-xs text-blue-600">
                Currently assigned
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Select value={selectedLevelId} onValueChange={setSelectedLevelId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>None</span>
                </div>
              </SelectItem>
              {levels.map((level) => (
                <SelectItem key={level.name} value={level.name}>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>{level.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleAssignLevel}
              disabled={!selectedLevelId || isAssigning || !effectiveDeviceConfig}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                currentLevel ? "Update Level" : "Assign Level"
              )}
            </Button>
            
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isAssigning}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {!currentLevel && !isEditing && (
        <div className="text-center py-4">
          <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-3">No level assigned to {deviceName}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditClick}
            disabled={!effectiveDeviceConfig}
          >
            Assign Level
          </Button>
        </div>
      )}
    </div>
  );
};
